import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Loader2, MessageCircleWarning, Search, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending_review: { label: 'Pendiente', color: 'text-yellow-500 bg-yellow-500/10' },
  needs_info: { label: 'Falta info', color: 'text-orange-500 bg-orange-500/10' },
  accepted: { label: 'Aceptada', color: 'text-blue-500 bg-blue-500/10' },
  in_progress: { label: 'En proceso', color: 'text-purple-500 bg-purple-500/10' },
  ready_to_pay: { label: 'Listo pagar', color: 'text-emerald-500 bg-emerald-500/10' },
  delivered: { label: 'Entregada', color: 'text-green-500 bg-green-500/10' },
  closed: { label: 'Cerrada', color: 'text-zinc-500 bg-zinc-500/10' },
  cancelled: { label: 'Cancelada', color: 'text-red-500 bg-red-500/10' },
};

const PRIORITY_MAP = { low: 'Baja', normal: 'Normal', high: 'Alta', urgent: 'Urgente' };

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.value = 0.3;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.stop(ctx.currentTime + 0.5);
  } catch {}
};

const AdminTicketsPage = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get('status') || (searchParams.get('waiting') ? 'waiting' : 'all'));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const prevWaitingIds = useRef(new Set());

  const fetchTickets = async (silent = false) => {
    try {
      const params = {};
      if (filter !== 'all' && filter !== 'waiting') params.status = filter;
      if (activeSearch) params.search = activeSearch;
      const res = await axios.get(`${API}/admin/tickets`, { headers: { Authorization: `Bearer ${token}` }, params });
      let list = res.data.tickets || [];
      if (filter === 'waiting') list = list.filter(t => t.client_waiting);

      // Check for new waiting tickets (notification sound)
      if (silent) {
        const newWaiting = list.filter(t => t.client_waiting && !prevWaitingIds.current.has(t.id));
        if (newWaiting.length > 0) {
          playNotificationSound();
          newWaiting.forEach(t => {
            toast.warning(`⚠ ${t.user_company} espera respuesta en ${t.ticket_number}`, { duration: 10000 });
          });
        }
      }
      prevWaitingIds.current = new Set(list.filter(t => t.client_waiting).map(t => t.id));
      setTickets(list);
    } catch {}
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, [filter, activeSearch]);

  // Poll every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchTickets(true), 15000);
    return () => clearInterval(interval);
  }, [filter, activeSearch, token]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchQuery.trim());
  };

  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
  };

  const deleteTicket = async (e, ticketId, ticketNumber) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar ${ticketNumber}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${ticketNumber} eliminado`);
      setTickets(prev => prev.filter(t => t.id !== ticketId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const waitingCount = tickets.filter(t => t.client_waiting).length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-tickets-page">
        <h1 className="font-heading text-3xl font-bold text-white mb-6">GESTIÓN SOLICITUDES</h1>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-4" data-testid="ticket-search-form">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar por ticket, matrícula, marca, modelo, cliente, ECU..."
              className="w-full bg-surface border border-white/10 rounded-md pl-10 pr-10 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-primary/50 focus:outline-none"
              data-testid="ticket-search-input"
            />
            {searchQuery && (
              <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button type="submit" className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-md text-sm font-medium" data-testid="ticket-search-btn">
            <Search className="w-4 h-4" />
          </button>
        </form>
        {activeSearch && (
          <div className="mb-3 flex items-center gap-2">
            <span className="text-zinc-500 text-xs">Buscando:</span>
            <span className="text-primary text-xs font-mono bg-primary/10 px-2 py-0.5 rounded">{activeSearch}</span>
            <button onClick={clearSearch} className="text-zinc-500 hover:text-white text-xs">(limpiar)</button>
          </div>
        )}

        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${filter === 'all' ? 'bg-primary text-white' : 'bg-surface border border-white/10 text-zinc-400'}`}>Todas</button>
          <button onClick={() => setFilter('waiting')} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap flex items-center gap-1.5 ${filter === 'waiting' ? 'bg-red-500 text-white' : 'bg-surface border border-white/10 text-zinc-400'}`}>
            <MessageCircleWarning className="w-3 h-3" />Esperando ({waitingCount})
          </button>
          {Object.entries(STATUS_MAP).map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${filter === k ? 'bg-primary text-white' : 'bg-surface border border-white/10 text-zinc-400'}`}>{v.label}</button>
          ))}
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div> : (
          <div className="bg-surface border border-white/10 rounded-md divide-y divide-white/5">
            {tickets.map(t => {
              const s = STATUS_MAP[t.status] || STATUS_MAP.pending_review;
              return (
                <Link key={t.id} to={`/admin/tickets/${t.id}`}
                  className={`flex items-center gap-4 p-4 hover:bg-white/5 transition-colors ${t.client_waiting ? 'bg-red-500/5 border-l-2 border-l-red-500' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-zinc-500">{t.ticket_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</span>
                      {t.client_waiting && (
                        <span className="text-xs px-2 py-0.5 rounded-full text-red-400 bg-red-500/10 flex items-center gap-1 animate-pulse">
                          <MessageCircleWarning className="w-3 h-3" />Esperando respuesta
                        </span>
                      )}
                      {t.priority && t.priority !== 'normal' && <span className={`text-xs px-2 py-0.5 rounded-full ${t.priority === 'urgent' ? 'text-red-400 bg-red-500/10' : t.priority === 'high' ? 'text-orange-400 bg-orange-500/10' : 'text-zinc-400 bg-zinc-500/10'}`}>{PRIORITY_MAP[t.priority]}</span>}
                      {t.payment_status === 'paid' && <span className="text-xs px-2 py-0.5 rounded-full text-green-400 bg-green-500/10">Pagado</span>}
                    </div>
                    <p className="text-white text-sm mt-1">{t.vehicle_brand} {t.vehicle_model} ({t.vehicle_year}) - {t.service_name}</p>
                    <p className="text-zinc-500 text-xs mt-0.5">{t.user_company} · {t.ecu_type} · {t.unit_type?.toUpperCase()} · {t.reading_tool}
                      {t.reading_type === 'slave' && <span className="ml-1 text-orange-400 font-medium">[SLAVE]</span>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 flex items-center gap-2">
                    <div>
                      {(t.price || t.service_base_price) && <p className="font-mono text-primary font-bold">{t.price || t.service_base_price}€</p>}
                      <p className="text-zinc-600 text-xs">{new Date(t.created_at).toLocaleDateString('es-ES')}</p>
                    </div>
                    <button onClick={(e) => deleteTicket(e, t.id, t.ticket_number)}
                      className="p-2 text-zinc-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      title="Eliminar ticket" data-testid={`delete-ticket-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Link>
              );
            })}
            {tickets.length === 0 && <p className="text-zinc-500 text-center py-8">Sin solicitudes</p>}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminTicketsPage;
